import { useState } from 'react';
import { errorMessage, fieldError } from '@/api/errors';

/**
 * A staff write checked first: `check()` sends the form's body with `dry_run` and keeps the answer as
 * the preview; `commit()` sends it for real. `body` is everything the request depends on: the preview
 * belongs to the body it was made for, so a form that changes after the check has no preview until it
 * is checked again. A refusal lands on the field the backend names (`errors[field]`) or, naming none,
 * in `general`.
 */
export function useRehearsal<TResult>(send: (dryRun: boolean) => Promise<TResult>, body: unknown) {
  const key = JSON.stringify(body);
  const [checked, setChecked] = useState<{ key: string; result: TResult } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [general, setGeneral] = useState<string | null>(null);
  const [pending, setPending] = useState<'check' | 'commit' | null>(null);

  const run = async (dryRun: boolean): Promise<TResult | null> => {
    setErrors({});
    setGeneral(null);
    setPending(dryRun ? 'check' : 'commit');
    try {
      const result = await send(dryRun);
      if (dryRun) setChecked({ key, result });
      return result;
    } catch (err) {
      setChecked(null);
      const f = fieldError(err);
      if (f) setErrors({ [f.field]: f.message });
      else setGeneral(errorMessage(err));
      return null;
    } finally {
      setPending(null);
    }
  };

  const reset = () => {
    setChecked(null);
    setErrors({});
    setGeneral(null);
  };

  return {
    /** The rehearsed result for the body as it is now, or null. */
    preview: checked?.key === key ? checked.result : null,
    errors,
    general,
    pending,
    check: () => run(true),
    commit: () => run(false),
    reset,
  };
}
